import { Button } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import type { GeoblockRule } from '@/models/Geoblock';

import VNPortlet from '../VNPortlet/VNPortlet';
import VNSelect from '../VNSelect/VNSelect';
import type { EFFECTS } from './GeoblockConstants';
import { EFFECT_OPTIONS } from './GeoblockConstants';
import GeoblockImportExport from './GeoblockImportExport';
import GeoblockLocationBrowser from './GeoblockLocationBrowser';

type Props = {
  name: string;
};

const GeoblockRuleBuilder = ({ name }: Props) => {
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  const { watch, setValue } = useFormContext();
  const rules = watch(`${name}.rules`);
  const action = watch('tmp.geoblock.action') as keyof typeof EFFECTS;
  const geographyLevel = watch('tmp.geoblock.geographyLevel') as string;

  // to filter results
  const existingValues: string[] = useMemo(
    () => rules.find((rule: GeoblockRule) => rule.effect === action && rule.type === geographyLevel)?.values || [],
    [rules, action, geographyLevel]
  );

  const handleAddRule = useCallback(() => {
    const merged = Array.from(new Set([...existingValues, ...selectedValues])); // unique values
    const ruleIndex = rules.findIndex((rule: GeoblockRule) => rule.effect === action && rule.type === geographyLevel);
    const updatedRules = JSON.parse(JSON.stringify(rules)); // deep copy to avoid direct state mutation

    if (ruleIndex !== -1) {
      updatedRules[ruleIndex].values = merged;
    }

    setValue(`${name}.rules`, updatedRules, { shouldDirty: true, shouldValidate: true });
    setSelectedValues([]);
    setValue(`tmp.geoblock.testLocation`, ''); // Clear test location on rule change
  }, [rules, selectedValues, existingValues, setValue, name, action, geographyLevel]);

  useEffect(() => {
    // init tmp values
    setValue('tmp.geoblock.action', 'Deny');
    setValue('tmp.geoblock.geographyLevel', 'Country');
  }, [setValue]);

  return (
    <VNPortlet
      title={
        <div className="portlet-title">
          <span className="step-number">2</span> Rule Builder
        </div>
      }
    >
      <div className="rule-builder-fields">
        <VNSelect label="Action" name="tmp.geoblock.action" options={EFFECT_OPTIONS} />

        <div className="rule-builder-helper">{EFFECT_OPTIONS.find((option) => option.value === action)?.ruleBuilderHelp}</div>

        {/* <VNSelect label="Geography Level" name="tmp.geoblock.geographyLevel" options={GEOGRAPHY_LEVEL_OPTIONS} /> */}

        <GeoblockLocationBrowser
          geographyLevel={geographyLevel}
          selectedValues={selectedValues}
          onSelectionChange={setSelectedValues}
          existingValues={existingValues}
        />

        <div className="rule-builder-actions">
          <Button color="primary" variant="contained" fullWidth disabled={!selectedValues.length} onClick={handleAddRule}>
            Add Rule
          </Button>
          <GeoblockImportExport name={name} />
        </div>
      </div>
    </VNPortlet>
  );
};

export default GeoblockRuleBuilder;
